/**
 * LAS FRASES DE TENIS DE LA PANTALLA DE INICIO.
 *
 * Antes, debajo del «Hola, Javier» decía «Esto es lo tuyo por ahora.» — una
 * frase que solo tenía sentido el día que se escribió, cuando la pantalla
 * estaba a medio hacer. Ahora sale una frase de tenis distinta cada vez.
 *
 * UN ARCHIVO POR IDIOMA Y CARGADOS APARTE, no dentro de los catálogos de
 * traducción. Son doscientas cincuenta frases en nueve idiomas: metidas en
 * `es.json` y compañía, cada persona se bajaría los nueve catálogos enteros
 * —los catálogos van juntos en el paquete principal— para leer una frase.
 * Acá se baja solo el idioma que se está usando, y recién cuando se abre
 * Inicio.
 *
 * EL FALLO NO SE VE. Si el archivo no carga —red que se cortó, idioma que
 * todavía no existe— la franja sale sin frase, igual que salía antes de
 * este cambio. Una frase motivacional no vale un mensaje de error.
 */
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type SupportedLocale } from '@tennisup/contracts';

/**
 * Los archivos, sin abrir.
 *
 * `import.meta.glob` sin `eager` deja nueve funciones que devuelven una
 * promesa: Vite parte cada archivo en su propio pedazo y no baja ninguno
 * hasta que se lo llama. Con `eager: true` volverían al paquete principal y
 * el ahorro desaparecería.
 */
const ARCHIVOS = import.meta.glob<{ FRASES: readonly string[] }>('./locales/*.ts');

/** Qué archivo le toca a un idioma, o `null` si ese idioma no tiene. */
function rutaDe(locale: string): string | null {
  const ruta = `./locales/${locale}.ts`;
  return ruta in ARCHIVOS ? ruta : null;
}

/**
 * Las frases de un idioma. Lista vacía si no hay.
 *
 * CAE AL ESPAÑOL antes de rendirse: un idioma nuevo, o uno regional que
 * todavía no tiene archivo, muestra la frase en español en vez de dejar la
 * franja a medias. Es lo mismo que hace `i18next` con las claves que faltan.
 */
export async function cargarFrases(locale: string): Promise<readonly string[]> {
  // `i18next` puede entregar «es-CR» o «en-US»; los archivos son «es» y «en».
  // Se prueba primero el nombre entero —«zh-CN» SÍ es un archivo— y después
  // la parte de antes del guion.
  const base = locale.split('-')[0] ?? locale;
  const ruta = rutaDe(locale) ?? rutaDe(base) ?? rutaDe(DEFAULT_LOCALE);
  if (ruta === null) return [];
  try {
    const modulo = await ARCHIVOS[ruta]?.();
    return modulo?.FRASES ?? [];
  } catch {
    // Ver la cabecera: una frase que no carga no es un error que contar.
    return [];
  }
}

/** Los idiomas que tienen archivo de frases. Lo usa la prueba de paridad. */
export function idiomasConFrases(): SupportedLocale[] {
  return SUPPORTED_LOCALES.filter((locale) => rutaDe(locale) !== null);
}

/**
 * CUÁL SALIÓ LA VEZ PASADA, para no repetirla dos veces seguidas.
 *
 * Al azar de verdad, dos visitas seguidas repiten frase una vez cada 250 —y
 * cuando pasa, se lee como que la pantalla se trabó, no como una casualidad.
 * Guardarlo en un módulo y no en `localStorage` es a propósito: lo que hay
 * que evitar es el «otra vez la misma» de volver a Inicio dentro de la misma
 * sesión, no llevar la cuenta entre días.
 */
let última: string | null = null;

/**
 * Una frase al azar, distinta de la anterior.
 *
 * `azar` entra por parámetro para que la prueba pueda fijarlo: probar algo
 * aleatorio con `Math.random` de verdad es probar la suerte.
 */
export function frasAlAzar(
  frases: readonly string[],
  azar: () => number = Math.random,
): string | null {
  if (frases.length === 0) return null;
  // Con una sola frase no hay nada que evitar, y buscar «otra» sería un
  // bucle infinito disfrazado.
  if (frases.length === 1) return frases[0] ?? null;

  const candidatas = frases.filter((frase) => frase !== última);
  const elegida = candidatas[Math.floor(azar() * candidatas.length)] ?? candidatas[0] ?? null;
  última = elegida;
  return elegida;
}

/** Solo para las pruebas: olvida cuál salió la última vez. */
export function olvidarLaÚltima(): void {
  última = null;
}
